import { useState, useMemo, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import {
  Euro, Plus, Check, ChevronLeft, ChevronRight, Loader2, Pencil, Trash2,
  X, Save, RotateCcw, Truck, CreditCard, Users, Building, Monitor,
  Megaphone, Briefcase, MoreHorizontal, AlertCircle, CheckCircle2,
  Clock, ChevronDown, ChevronUp, Package, Receipt,
} from 'lucide-react'

// ── Icon map ──────────────────────────────────────────────────

const iconMap: Record<string, React.ElementType> = {
  Users, Building, Monitor, Megaphone, Truck, Briefcase, CreditCard,
  MoreHorizontal, Package, Receipt,
}

function CategoryIcon({ icon, className = 'w-4 h-4' }: { icon: string; className?: string }) {
  const Icon = iconMap[icon] || MoreHorizontal
  return <Icon className={className} />
}

// ── Types ─────────────────────────────────────────────────────

interface Category {
  id: number; code: string; name: string; icon: string; sort_order: number
}

interface RecurringExpense {
  id: number; category_id: number; name: string; default_amount: number
  is_active: number; notes: string | null
  category_code: string; category_name: string; category_icon: string
}

interface MonthlyExpense {
  id: number; year_month: string; recurring_expense_id: number | null
  category_id: number; name: string; amount: number; notes: string | null
  category_code: string; category_name: string; category_icon: string
  recurring_name: string | null
}

interface Closure {
  year_month: string; status: string; confirmed_at?: string; total_amount?: number
}

interface LogisticsData {
  year_month: string; shipping_revenue: number
  carriers: { carrier_code: string; carrier_name: string; shipments: number; cost: number; cost_per_shipment: number }[]
  packaging_cost: number; total_shipping_cost: number; total_shipments: number; logistics_result: number
}

interface SummaryMonth {
  year_month: string; total: number; closure_status: string | null
}

// ── Helpers ───────────────────────────────────────────────────

const fmtCur = (v: number) => v.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })
const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']

function getYearMonth(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function parseYM(ym: string) {
  const [y, m] = ym.split('-').map(Number)
  return { year: y, month: m }
}

function prevMonth(ym: string) {
  const { year, month } = parseYM(ym)
  return month === 1 ? `${year - 1}-12` : `${year}-${String(month - 1).padStart(2, '0')}`
}

function nextMonth(ym: string) {
  const { year, month } = parseYM(ym)
  return month === 12 ? `${year + 1}-01` : `${year}-${String(month + 1).padStart(2, '0')}`
}

function monthLabel(ym: string) {
  const { year, month } = parseYM(ym)
  return `${monthNames[month - 1]} ${year}`
}

// ── Main Page ─────────────────────────────────────────────────

type Tab = 'monthly' | 'recurring' | 'summary'

export default function ExpensesPage() {
  const [tab, setTab] = useState<Tab>('monthly')
  const [selectedMonth, setSelectedMonth] = useState(getYearMonth(new Date()))

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Receipt className="w-7 h-7 text-amber-500" />
          Gastos de Funcionamiento
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Gastos fijos y operativos del negocio (sin incluir compra de mercaderias)
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1 w-fit">
        {([
          ['monthly', 'Mensual'],
          ['recurring', 'Gastos Recurrentes'],
          ['summary', 'Resumen Anual'],
        ] as [Tab, string][]).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === key
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'monthly' && <MonthlyTab selectedMonth={selectedMonth} onMonthChange={setSelectedMonth} />}
      {tab === 'recurring' && <RecurringTab />}
      {tab === 'summary' && <SummaryTab />}
    </div>
  )
}

// ── Monthly Tab ───────────────────────────────────────────────

function MonthlyTab({ selectedMonth, onMonthChange }: { selectedMonth: string; onMonthChange: (ym: string) => void }) {
  const qc = useQueryClient()
  const [showAdd, setShowAdd] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editAmount, setEditAmount] = useState('')
  const [editNotes, setEditNotes] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['expenses-monthly', selectedMonth],
    queryFn: () => api.get(`/expenses/monthly/${selectedMonth}`).then(r => r.data),
  })

  const { data: logisticsData } = useQuery({
    queryKey: ['expenses-logistics', selectedMonth],
    queryFn: () => api.get(`/expenses/logistics/${selectedMonth}`).then(r => r.data as LogisticsData),
  })

  const prepareMut = useMutation({
    mutationFn: () => api.post(`/expenses/monthly/${selectedMonth}/prepare`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['expenses-monthly', selectedMonth] }),
  })

  const confirmMut = useMutation({
    mutationFn: () => api.post(`/expenses/monthly/${selectedMonth}/confirm`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['expenses-monthly', selectedMonth] }),
  })

  const reopenMut = useMutation({
    mutationFn: () => api.post(`/expenses/monthly/${selectedMonth}/reopen`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['expenses-monthly', selectedMonth] }),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, amount, notes }: { id: number; amount: number; notes?: string }) =>
      api.put(`/expenses/monthly/expense/${id}`, { amount, notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expenses-monthly', selectedMonth] })
      setEditingId(null)
    },
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => api.delete(`/expenses/monthly/expense/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['expenses-monthly', selectedMonth] }),
  })

  const expenses: MonthlyExpense[] = data?.expenses || []
  const closure: Closure = data?.closure || { year_month: selectedMonth, status: 'pending' }
  const total: number = data?.total || 0
  const isConfirmed = closure.status === 'confirmed'
  const hasExpenses = expenses.length > 0

  // Group by category
  const grouped = useMemo(() => {
    const map = new Map<string, { category: { code: string; name: string; icon: string }; items: MonthlyExpense[]; subtotal: number }>()
    for (const e of expenses) {
      if (!map.has(e.category_code)) {
        map.set(e.category_code, {
          category: { code: e.category_code, name: e.category_name, icon: e.category_icon },
          items: [], subtotal: 0,
        })
      }
      const g = map.get(e.category_code)!
      g.items.push(e)
      g.subtotal += e.amount
    }
    return Array.from(map.values())
  }, [expenses])

  const startEdit = (e: MonthlyExpense) => {
    setEditingId(e.id)
    setEditAmount(String(e.amount))
    setEditNotes(e.notes || '')
  }

  const saveEdit = () => {
    if (editingId == null) return
    updateMut.mutate({ id: editingId, amount: parseFloat(editAmount) || 0, notes: editNotes || undefined })
  }

  return (
    <div className="space-y-4">
      {/* Month navigator */}
      <div className="flex items-center justify-between bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700">
        <button onClick={() => onMonthChange(prevMonth(selectedMonth))} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="text-center">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{monthLabel(selectedMonth)}</h2>
          <div className="flex items-center gap-2 justify-center mt-1">
            {isConfirmed ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400 px-2 py-0.5 rounded-full">
                <CheckCircle2 className="w-3 h-3" /> Confirmado
              </span>
            ) : hasExpenses ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400 px-2 py-0.5 rounded-full">
                <Clock className="w-3 h-3" /> Pendiente de confirmar
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full">
                Sin datos
              </span>
            )}
          </div>
        </div>
        <button onClick={() => onMonthChange(nextMonth(selectedMonth))} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : !hasExpenses ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl p-8 text-center border border-slate-200 dark:border-slate-700">
          <AlertCircle className="w-10 h-10 mx-auto text-slate-400 mb-3" />
          <p className="text-slate-600 dark:text-slate-400 mb-4">No hay gastos registrados para {monthLabel(selectedMonth)}</p>
          <button
            onClick={() => prepareMut.mutate()}
            disabled={prepareMut.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {prepareMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Preparar mes (copiar gastos recurrentes)
          </button>
        </div>
      ) : (
        <>
          {/* Total bar */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-4 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Total gastos {monthLabel(selectedMonth)}</p>
                <p className="text-3xl font-bold">{fmtCur(total)}</p>
              </div>
              <div className="flex gap-2">
                {!isConfirmed && (
                  <>
                    <button
                      onClick={() => setShowAdd(true)}
                      className="px-3 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors"
                    >
                      <Plus className="w-4 h-4 inline mr-1" />Gasto puntual
                    </button>
                    <button
                      onClick={() => confirmMut.mutate()}
                      disabled={confirmMut.isPending}
                      className="px-3 py-2 bg-white text-amber-600 hover:bg-amber-50 rounded-lg text-sm font-medium transition-colors"
                    >
                      <Check className="w-4 h-4 inline mr-1" />Confirmar mes
                    </button>
                  </>
                )}
                {isConfirmed && (
                  <button
                    onClick={() => reopenMut.mutate()}
                    className="px-3 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors"
                  >
                    <RotateCcw className="w-4 h-4 inline mr-1" />Reabrir
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Expense groups */}
          <div className="space-y-3">
            {grouped.map(g => (
              <ExpenseGroup
                key={g.category.code}
                category={g.category}
                items={g.items}
                subtotal={g.subtotal}
                isConfirmed={isConfirmed}
                editingId={editingId}
                editAmount={editAmount}
                editNotes={editNotes}
                onStartEdit={startEdit}
                onSaveEdit={saveEdit}
                onCancelEdit={() => setEditingId(null)}
                onEditAmountChange={setEditAmount}
                onEditNotesChange={setEditNotes}
                onDelete={(id) => { if (confirm('Eliminar este gasto?')) deleteMut.mutate(id) }}
              />
            ))}
          </div>

          {/* Logistics cross-reference */}
          {logisticsData && <LogisticsPanel data={logisticsData} />}
        </>
      )}

      {/* Add one-time expense modal */}
      {showAdd && <AddExpenseModal yearMonth={selectedMonth} onClose={() => setShowAdd(false)} />}
    </div>
  )
}

// ── Expense Group ─────────────────────────────────────────────

function ExpenseGroup({
  category, items, subtotal, isConfirmed,
  editingId, editAmount, editNotes,
  onStartEdit, onSaveEdit, onCancelEdit, onEditAmountChange, onEditNotesChange, onDelete,
}: {
  category: { code: string; name: string; icon: string }
  items: MonthlyExpense[]; subtotal: number; isConfirmed: boolean
  editingId: number | null; editAmount: string; editNotes: string
  onStartEdit: (e: MonthlyExpense) => void; onSaveEdit: () => void; onCancelEdit: () => void
  onEditAmountChange: (v: string) => void; onEditNotesChange: (v: string) => void
  onDelete: (id: number) => void
}) {
  const [open, setOpen] = useState(true)

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-750 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <CategoryIcon icon={category.icon} className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          </div>
          <span className="font-medium text-slate-900 dark:text-white">{category.name}</span>
          <span className="text-xs text-slate-400">{items.length} {items.length === 1 ? 'gasto' : 'gastos'}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-semibold text-slate-900 dark:text-white">{fmtCur(subtotal)}</span>
          {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-100 dark:border-slate-700">
          {items.map(item => (
            <div key={item.id} className="flex items-center justify-between px-4 py-3 border-b border-slate-50 dark:border-slate-700/50 last:border-b-0 hover:bg-slate-50/50 dark:hover:bg-slate-750/50">
              {editingId === item.id ? (
                <div className="flex items-center gap-2 w-full">
                  <span className="text-sm text-slate-700 dark:text-slate-300 min-w-0 truncate flex-1">{item.name}</span>
                  <input
                    type="number"
                    step="0.01"
                    value={editAmount}
                    onChange={e => onEditAmountChange(e.target.value)}
                    className="w-28 px-2 py-1 text-sm border rounded-md dark:bg-slate-700 dark:border-slate-600 text-right"
                    autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') onSaveEdit(); if (e.key === 'Escape') onCancelEdit() }}
                  />
                  <input
                    type="text"
                    value={editNotes}
                    onChange={e => onEditNotesChange(e.target.value)}
                    placeholder="Notas..."
                    className="w-32 px-2 py-1 text-sm border rounded-md dark:bg-slate-700 dark:border-slate-600"
                    onKeyDown={e => { if (e.key === 'Enter') onSaveEdit(); if (e.key === 'Escape') onCancelEdit() }}
                  />
                  <button onClick={onSaveEdit} className="p-1 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded"><Save className="w-4 h-4" /></button>
                  <button onClick={onCancelEdit} className="p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <>
                  <div className="min-w-0 flex-1">
                    <span className="text-sm text-slate-700 dark:text-slate-300">{item.name}</span>
                    {item.notes && <span className="text-xs text-slate-400 ml-2">({item.notes})</span>}
                    {item.recurring_expense_id == null && (
                      <span className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded ml-2">puntual</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 dark:text-white tabular-nums">{fmtCur(item.amount)}</span>
                    {!isConfirmed && (
                      <>
                        <button onClick={() => onStartEdit(item)} className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"><Pencil className="w-3.5 h-3.5" /></button>
                        {item.recurring_expense_id == null && (
                          <button onClick={() => onDelete(item.id)} className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Logistics Panel ───────────────────────────────────────────

function LogisticsPanel({ data }: { data: LogisticsData }) {
  const [open, setOpen] = useState(true)

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-750"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <Truck className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <span className="font-medium text-slate-900 dark:text-white">Balance Logistico</span>
        </div>
        <div className="flex items-center gap-3">
          <span className={`font-semibold ${data.logistics_result >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {data.logistics_result >= 0 ? '+' : ''}{fmtCur(data.logistics_result)}
          </span>
          {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-100 dark:border-slate-700 p-4 space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3">
              <p className="text-xs text-emerald-600 dark:text-emerald-400">Portes cobrados</p>
              <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">{fmtCur(data.shipping_revenue)}</p>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
              <p className="text-xs text-red-600 dark:text-red-400">Coste envios + embalaje</p>
              <p className="text-lg font-bold text-red-700 dark:text-red-300">{fmtCur(data.total_shipping_cost)}</p>
            </div>
            <div className={`rounded-lg p-3 ${data.logistics_result >= 0 ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-amber-50 dark:bg-amber-900/20'}`}>
              <p className="text-xs text-slate-600 dark:text-slate-400">Resultado</p>
              <p className={`text-lg font-bold ${data.logistics_result >= 0 ? 'text-blue-700 dark:text-blue-300' : 'text-amber-700 dark:text-amber-300'}`}>
                {data.logistics_result >= 0 ? '+' : ''}{fmtCur(data.logistics_result)}
              </p>
            </div>
          </div>

          {/* Carrier table */}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-500 uppercase border-b border-slate-200 dark:border-slate-700">
                <th className="text-left py-2">Transportista</th>
                <th className="text-right py-2">Envios</th>
                <th className="text-right py-2">Coste factura</th>
                <th className="text-right py-2">Coste/envio</th>
              </tr>
            </thead>
            <tbody>
              {data.carriers.map(c => (
                <tr key={c.carrier_code} className="border-b border-slate-50 dark:border-slate-700/50">
                  <td className="py-2 font-medium text-slate-700 dark:text-slate-300">{c.carrier_name}</td>
                  <td className="py-2 text-right tabular-nums">{c.shipments}</td>
                  <td className="py-2 text-right tabular-nums">{c.cost > 0 ? fmtCur(c.cost) : <span className="text-slate-400">-</span>}</td>
                  <td className="py-2 text-right tabular-nums">{c.cost_per_shipment > 0 ? fmtCur(c.cost_per_shipment) : <span className="text-slate-400">-</span>}</td>
                </tr>
              ))}
              {data.packaging_cost > 0 && (
                <tr className="border-b border-slate-50 dark:border-slate-700/50">
                  <td className="py-2 font-medium text-slate-700 dark:text-slate-300">Embalaje</td>
                  <td className="py-2 text-right">-</td>
                  <td className="py-2 text-right tabular-nums">{fmtCur(data.packaging_cost)}</td>
                  <td className="py-2 text-right">-</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="font-semibold text-slate-900 dark:text-white">
                <td className="pt-2">Total</td>
                <td className="pt-2 text-right">{data.total_shipments}</td>
                <td className="pt-2 text-right">{fmtCur(data.total_shipping_cost)}</td>
                <td className="pt-2 text-right">{data.total_shipments > 0 ? fmtCur(data.total_shipping_cost / data.total_shipments) : '-'}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Add One-Time Expense Modal ────────────────────────────────

function AddExpenseModal({ yearMonth, onClose }: { yearMonth: string; onClose: () => void }) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [amount, setAmount] = useState('')
  const [catId, setCatId] = useState<number | ''>('')
  const [notes, setNotes] = useState('')

  const { data: catData } = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => api.get('/expenses/categories').then(r => r.data),
  })

  const addMut = useMutation({
    mutationFn: () => api.post(`/expenses/monthly/${yearMonth}/one-time`, {
      category_id: catId, name, amount: parseFloat(amount), notes: notes || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expenses-monthly', yearMonth] })
      onClose()
    },
  })

  const categories: Category[] = catData?.categories || []

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-white">Nuevo gasto puntual</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Categoria</label>
            <select value={catId} onChange={e => setCatId(Number(e.target.value))} className="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 text-sm">
              <option value="">Seleccionar...</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Concepto</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 text-sm" placeholder="Ej: Reparacion impresora" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Importe</label>
            <input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} className="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 text-sm" placeholder="0.00" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notas (opcional)</label>
            <input type="text" value={notes} onChange={e => setNotes(e.target.value)} className="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 text-sm" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">Cancelar</button>
          <button
            onClick={() => addMut.mutate()}
            disabled={!catId || !name || !amount || addMut.isPending}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {addMut.isPending ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Recurring Tab ─────────────────────────────────────────────

function RecurringTab() {
  const qc = useQueryClient()
  const [showAdd, setShowAdd] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [editAmount, setEditAmount] = useState('')
  const [editCatId, setEditCatId] = useState<number>(0)

  const { data } = useQuery({
    queryKey: ['expenses-recurring'],
    queryFn: () => api.get('/expenses/recurring?active_only=false').then(r => r.data),
  })

  const { data: catData } = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => api.get('/expenses/categories').then(r => r.data),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, ...body }: { id: number; name?: string; default_amount?: number; category_id?: number; is_active?: number }) =>
      api.put(`/expenses/recurring/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['expenses-recurring'] }); setEditingId(null) },
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => api.delete(`/expenses/recurring/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['expenses-recurring'] }),
  })

  const expenses: RecurringExpense[] = data?.expenses || []
  const categories: Category[] = catData?.categories || []

  // Group by category
  const grouped = useMemo(() => {
    const map = new Map<string, { catName: string; icon: string; items: RecurringExpense[] }>()
    for (const e of expenses) {
      if (!map.has(e.category_code)) map.set(e.category_code, { catName: e.category_name, icon: e.category_icon, items: [] })
      map.get(e.category_code)!.items.push(e)
    }
    return Array.from(map.values())
  }, [expenses])

  const totalMonthly = expenses.filter(e => e.is_active).reduce((s, e) => s + e.default_amount, 0)

  const startEdit = (e: RecurringExpense) => {
    setEditingId(e.id); setEditName(e.name); setEditAmount(String(e.default_amount)); setEditCatId(e.category_id)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">Total mensual estimado: <span className="font-semibold text-slate-900 dark:text-white">{fmtCur(totalMonthly)}</span></p>
        </div>
        <button onClick={() => setShowAdd(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
          <Plus className="w-4 h-4" /> Nuevo gasto recurrente
        </button>
      </div>

      {grouped.map(g => (
        <div key={g.catName} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3 p-4 border-b border-slate-100 dark:border-slate-700">
            <CategoryIcon icon={g.icon} className="w-4 h-4 text-amber-600" />
            <span className="font-medium text-slate-900 dark:text-white">{g.catName}</span>
          </div>
          <div>
            {g.items.map(item => (
              <div key={item.id} className={`flex items-center justify-between px-4 py-3 border-b border-slate-50 dark:border-slate-700/50 last:border-b-0 ${!item.is_active ? 'opacity-50' : ''}`}>
                {editingId === item.id ? (
                  <div className="flex items-center gap-2 w-full">
                    <select value={editCatId} onChange={e => setEditCatId(Number(e.target.value))} className="px-2 py-1 text-sm border rounded-md dark:bg-slate-700 dark:border-slate-600">
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <input type="text" value={editName} onChange={e => setEditName(e.target.value)} className="flex-1 px-2 py-1 text-sm border rounded-md dark:bg-slate-700 dark:border-slate-600" />
                    <input type="number" step="0.01" value={editAmount} onChange={e => setEditAmount(e.target.value)} className="w-28 px-2 py-1 text-sm border rounded-md dark:bg-slate-700 dark:border-slate-600 text-right" />
                    <button onClick={() => updateMut.mutate({ id: item.id, name: editName, default_amount: parseFloat(editAmount), category_id: editCatId })} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"><Save className="w-4 h-4" /></button>
                    <button onClick={() => setEditingId(null)} className="p-1 text-slate-400 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <>
                    <span className="text-sm text-slate-700 dark:text-slate-300">{item.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900 dark:text-white tabular-nums">{fmtCur(item.default_amount)}</span>
                      <button onClick={() => startEdit(item)} className="p-1 text-slate-400 hover:text-blue-600 rounded"><Pencil className="w-3.5 h-3.5" /></button>
                      <button
                        onClick={() => updateMut.mutate({ id: item.id, is_active: item.is_active ? 0 : 1 })}
                        className={`p-1 rounded ${item.is_active ? 'text-emerald-500 hover:text-red-500' : 'text-slate-400 hover:text-emerald-500'}`}
                        title={item.is_active ? 'Desactivar' : 'Activar'}
                      >
                        {item.is_active ? <CheckCircle2 className="w-3.5 h-3.5" /> : <RotateCcw className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => { if (confirm('Eliminar gasto recurrente?')) deleteMut.mutate(item.id) }} className="p-1 text-slate-400 hover:text-red-600 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {showAdd && <AddRecurringModal categories={categories} onClose={() => setShowAdd(false)} />}
    </div>
  )
}

function AddRecurringModal({ categories, onClose }: { categories: Category[]; onClose: () => void }) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [amount, setAmount] = useState('')
  const [catId, setCatId] = useState<number | ''>('')
  const [notes, setNotes] = useState('')

  const addMut = useMutation({
    mutationFn: () => api.post('/expenses/recurring', {
      category_id: catId, name, amount: parseFloat(amount), notes: notes || undefined,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['expenses-recurring'] }); onClose() },
  })

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-white">Nuevo gasto recurrente</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Categoria</label>
            <select value={catId} onChange={e => setCatId(Number(e.target.value))} className="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 text-sm">
              <option value="">Seleccionar...</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Concepto</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 text-sm" placeholder="Ej: Nomina empleado" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Importe mensual</label>
            <input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} className="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 text-sm" placeholder="0.00" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notas (opcional)</label>
            <input type="text" value={notes} onChange={e => setNotes(e.target.value)} className="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 text-sm" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">Cancelar</button>
          <button
            onClick={() => addMut.mutate()}
            disabled={!catId || !name || !amount || addMut.isPending}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {addMut.isPending ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Summary Tab ───────────────────────────────────────────────

function SummaryTab() {
  const [year, setYear] = useState(String(new Date().getFullYear()))

  const { data } = useQuery({
    queryKey: ['expenses-summary', year],
    queryFn: () => api.get(`/expenses/summary?year=${year}`).then(r => r.data),
  })

  const { data: breakdownData } = useQuery({
    queryKey: ['expenses-breakdown', year],
    queryFn: () => api.get(`/expenses/category-breakdown?year=${year}`).then(r => r.data),
  })

  const months: SummaryMonth[] = data?.months || []
  const grandTotal: number = data?.grand_total || 0
  const breakdown: { code: string; name: string; icon: string; total: number; item_count: number }[] = breakdownData?.breakdown || []

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Ejercicio:</label>
        <select value={year} onChange={e => setYear(e.target.value)} className="px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 text-sm">
          {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="text-sm text-slate-500">Total anual: <span className="font-semibold text-slate-900 dark:text-white">{fmtCur(grandTotal)}</span></span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Monthly chart/table */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Gastos mensuales</h3>
          {months.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">Sin datos para {year}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 uppercase border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-2">Mes</th>
                  <th className="text-right py-2">Total</th>
                  <th className="text-center py-2">Estado</th>
                </tr>
              </thead>
              <tbody>
                {months.map(m => (
                  <tr key={m.year_month} className="border-b border-slate-50 dark:border-slate-700/50">
                    <td className="py-2 text-slate-700 dark:text-slate-300">{monthLabel(m.year_month)}</td>
                    <td className="py-2 text-right font-medium tabular-nums text-slate-900 dark:text-white">{fmtCur(m.total)}</td>
                    <td className="py-2 text-center">
                      {m.closure_status === 'confirmed' ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 inline" />
                      ) : (
                        <Clock className="w-4 h-4 text-amber-500 inline" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold text-slate-900 dark:text-white">
                  <td className="pt-2">Total {year}</td>
                  <td className="pt-2 text-right">{fmtCur(grandTotal)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {/* Category breakdown */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Desglose por categoria</h3>
          {breakdown.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">Sin datos para {year}</p>
          ) : (
            <div className="space-y-2">
              {breakdown.map(b => {
                const pct = grandTotal > 0 ? (b.total / grandTotal) * 100 : 0
                return (
                  <div key={b.code} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <CategoryIcon icon={b.icon} className="w-4 h-4 text-amber-600" />
                        <span className="text-slate-700 dark:text-slate-300">{b.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">{pct.toFixed(1)}%</span>
                        <span className="font-medium tabular-nums text-slate-900 dark:text-white">{fmtCur(b.total)}</span>
                      </div>
                    </div>
                    <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
